import React, { useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import { backendUrl } from '../../utils/socketClient';
import { supabaseClient } from '../../utils/supabaseClient';

interface WordCloudItem {
  text: string;
  value: number;
  category: string;
  sentiment: string;
  date: Date;
}

interface KeywordCloudProps {
  dateRange?: {
    start: Date;
    end: Date;
  };
  periodLabel?: string;
  onRefresh?: () => void;
  shopName?: string;
}

const KeywordCloud = forwardRef<
  { fetchKeywordData: () => void },
  KeywordCloudProps
>(({ dateRange, periodLabel, onRefresh, shopName }, ref) => {
  const [filteredKeywords, setFilteredKeywords] = useState<WordCloudItem[]>([]);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 日付を経過日数に変換する関数
  const getDaysAgo = (date: Date): number => {
    const now = new Date();
    return Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
  };

  // キーワードデータを取得する関数
  const fetchKeywordData = async () => {
    return new Promise<void>(async (resolve) => {
      if (!dateRange || !shopName || !periodLabel) {
        setError('日付範囲・期間ラベル・店舗名が必要です');
        resolve();
        return;
      }
      try {
        setIsRefreshing(true);
        setError(null);
        const requestBody = {
          startDate: getDaysAgo(dateRange.start),
          endDate: getDaysAgo(dateRange.end),
          periodLabel: periodLabel
        };
        const response = await fetch(`${backendUrl}/keywords/${encodeURIComponent(shopName)}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestBody),
        });
        const data = await response.json();
        if (!Array.isArray(data) || data.length === 0) {
          setFilteredKeywords([]);
          resolve();
          return;
        }
        const keywords: WordCloudItem[] = data.map((item: any) => ({
          text: item.text,
          value: item.value,
          category: item.category,
          sentiment: item.sentiment,
          date: new Date(item.date),
        }));
        setFilteredKeywords(keywords);
        if (onRefresh) {
          onRefresh();
        }
      } catch (error) {
        setError(error instanceof Error ? error.message : '予期せぬエラーが発生しました');
        setFilteredKeywords([]);
      } finally {
        setIsRefreshing(false);
        resolve();
      }
    });
  };

  // useEffectは削除またはコメントアウト
  useEffect(() => {
    fetchCachedKeywords();
  }, [dateRange, shopName]);

  // キーワードの色を取得
  const getColor = (category: string) => {
    const colors: { [key: string]: string } = {
      '料理': 'text-green-600',
      '接客': 'text-blue-800',
      '提供スピード': 'text-red-500',
      '価格': 'text-yellow-300',
      '雰囲気・空間': 'text-purple-900',
      '清潔さ': 'text-sky-300',
      'アクセス・立地': 'text-orange-800',
      '予約・注文システム': 'text-pink-400',
    };
    return colors[category] || 'text-black';
  };

  // キーワードのサイズを計算
  const getSize = (value: number) => {
    if (filteredKeywords.length === 0) return '1rem';
    const maxValue = Math.max(...filteredKeywords.map(k => k.value));
    const minSize = 0.8;
    const maxSize = 2.5;
    const size = minSize + ((value / maxValue) * (maxSize - minSize));
    return `${size}rem`;
  };

  // 配列をシャッフルする関数
  const shuffleArray = (array: WordCloudItem[]) => {
    const newArray = [...array];
    for (let i = newArray.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
    }
    return newArray;
  };

  // 外部からfetchKeywordDataを呼び出せるように
  useImperativeHandle(ref, () => ({
    fetchKeywordData,
  }));

  const fetchCachedKeywords = async () => {
    if (!shopName || !periodLabel) return;
    setIsRefreshing(true);
    setError(null);
    try {
      const { data, error } = await supabaseClient
        .from('keyword_analysis_cache')
        .select('keywords_json')
        .eq('shopname', shopName)
        .eq('period_label', periodLabel)
        .order('created_at', { ascending: false })
        .limit(1);
      if (data && data.length > 0 && data[0].keywords_json) {
        setFilteredKeywords(data[0].keywords_json);
      } else {
        setFilteredKeywords([]);
      }
    } catch (e) {
      setFilteredKeywords([]);
    }
    setIsRefreshing(false);
  };

  return (
    <div className="relative">
      {/* カテゴリ色の凡例 */}
      <div className="flex flex-wrap gap-4 mb-4 justify-center">
        <span className="flex items-center gap-1"><span className="inline-block w-4 h-4 rounded-full bg-green-600"></span>料理</span>
        <span className="flex items-center gap-1"><span className="inline-block w-4 h-4 rounded-full bg-blue-800"></span>接客</span>
        <span className="flex items-center gap-1"><span className="inline-block w-4 h-4 rounded-full bg-red-500"></span>提供スピード</span>
        <span className="flex items-center gap-1"><span className="inline-block w-4 h-4 rounded-full bg-yellow-300"></span>価格</span>
        <span className="flex items-center gap-1"><span className="inline-block w-4 h-4 rounded-full bg-purple-900"></span>雰囲気・空間</span>
        <span className="flex items-center gap-1"><span className="inline-block w-4 h-4 rounded-full bg-sky-300"></span>清潔さ</span>
        <span className="flex items-center gap-1"><span className="inline-block w-4 h-4 rounded-full bg-orange-800"></span>アクセス・立地</span>
        <span className="flex items-center gap-1"><span className="inline-block w-4 h-4 rounded-full bg-pink-400"></span>予約・注文システム</span>
        <span className="flex items-center gap-1"><span className="inline-block w-4 h-4 rounded-full bg-black"></span>その他</span>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-md">
          {error}
        </div>
      )}

      {isRefreshing && filteredKeywords.length === 0 ? (
        <div className="flex justify-center items-center h-48">
          <div className="text-gray-500">データを取得中...</div>
        </div>
      ) : filteredKeywords.length === 0 ? (
        <div className="flex justify-center items-center h-48">
          <div className="text-gray-500">キーワードが見つかりません</div>
        </div>
      ) : (
        <div className="flex flex-wrap gap-4 justify-center">
          {shuffleArray([...filteredKeywords]).map((keyword, index) => (
            <div
              key={index}
              className={`${getColor(keyword.category)} transition-transform hover:scale-110 cursor-pointer`}
              style={{ 
                fontSize: getSize(keyword.value),
                transform: `rotate(${Math.random() * 10 - 5}deg)`
              }}
              title={`出現回数: ${keyword.value} - 感情: ${keyword.sentiment}`}
            >
              {keyword.text}
            </div>
          ))}
        </div>
      )}
    </div>
  );
});

export default KeywordCloud;